import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Grid, IconButton, InputAdornment, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  CognitoIdentityProviderClient,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";

const poolData = {
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
};

const cognitoClient = new CognitoIdentityProviderClient({
  region: process.env.REACT_APP_AWS_REGION,
});

export default function ForgotPassword({setShowReset}) {
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1); 
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [showlogin, setShowLogin] = useState(false);
  const [showValidations, setShowValidations] = useState(false);
  const [passwordValidations, setPasswordValidations] = useState({
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
  });
  
  useEffect(() => {
    setShowReset(!showlogin);
  }, [setShowReset, showlogin]);

  useEffect(() => {
    if (newPassword !== confirmPassword) {
      setError("New Password and Confirm Password do not match.");
      return;
    } else {
      setError("");
    }
  }, [confirmPassword]);


  const handleForgotPassword = async () => {
    try {
      const command = new ForgotPasswordCommand({
        ClientId: poolData.ClientId,
        Username: email,
      });
      await cognitoClient.send(command);
      setStep(2);
    } catch (err) {
      alert("Error: " + (err.message || "Failed to send reset request"));
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !newPassword || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New Password and Confirm Password do not match.");
      return;
    } else {
      setError("");
    }

    if (!Object.values(passwordValidations).every(Boolean)) {
      setError("Password does not meet the required criteria.");
      return;
    }

    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: poolData.ClientId,
        Username: email,
        ConfirmationCode: resetCode,
        Password: newPassword,
      });
      await cognitoClient.send(command);
      alert("Password reset successful! Please log in.");
      setShowLogin(true) 
    } catch (err) {
      alert("Error: " + (err.message || "Failed to reset password"));
    }
  };

  const validatePassword = (password) => {
    setNewPassword(password);
    setPasswordValidations({
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
    });
  };
  const getValidationIcon = (isValid) => (isValid ? "✅" : "❌");

  return (
    <Grid container spacing={0}>
          <Grid item sm={12} md={3} lg={4} />
          <Grid item sm={12} md={6} lg={4}>
        <Box p={4} >
        <h1
            className="gradientText"
            style={{
              fontWeight: 900,
              fontSize: "36px",
              lineHeight: 1.2,
              textTransform: "capitalize",
              margin: 0,
            }}
          >Reset Your Password</h1>
          {step === 1 ? (
            <>
              <h4>Please enter your username to receive a reset code on your registered email.</h4>
              <TextField
                fullWidth
                label="Username"
                variant="standard"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button variant="contained" color="primary" onClick={handleForgotPassword} 
                style={{ margin: "16px 0 16px 0" }} disabled={!email}
                >
                  Send Reset Code
                </Button>
                </Box>
            </>
          ) : (
            <>
            &nbsp;
              <TextField
                fullWidth
                label="Reset Code"
                variant="standard"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
              />
              &nbsp;
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? "text" : "password"}
                variant="standard"
                value={newPassword}
                onFocus={() => setShowValidations(true)}
                onBlur={() => setShowValidations(false)}
                onChange={(e) => validatePassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {showValidations && <Box textAlign="left" mt={1}>
                <Typography variant="caption" color={passwordValidations.hasNumber ? "rgb(23 203 43)" : "red"}>
                  {getValidationIcon(passwordValidations.hasNumber)} Contains at least 1 number
                </Typography>
                <br />
                <Typography variant="caption" color={passwordValidations.hasSpecialChar ? "rgb(23 203 43)" : "red"}>
                  {getValidationIcon(passwordValidations.hasSpecialChar)} Contains at least 1 special character
                </Typography>
                <br />
                <Typography variant="caption" color={passwordValidations.hasUpperCase ? "rgb(23 203 43)" : "red"}>
                  {getValidationIcon(passwordValidations.hasUpperCase)} Contains at least 1 uppercase letter
                </Typography>
                <br />
                <Typography variant="caption" color={passwordValidations.hasLowerCase ? "rgb(23 203 43)" : "red"}>
                  {getValidationIcon(passwordValidations.hasLowerCase)} Contains at least 1 lowercase letter
                </Typography>
              </Box>}
              &nbsp;
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                variant="standard"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => {
                  if (newPassword !== confirmPassword) {
                    setError("New Password and Confirm Password do not match.");
                  } else {
                    setError("");
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {error && <p style={{ color: "red" }}>{error}</p>}
              <Box display="flex" justifyContent="flex-end">
                <Button variant="contained" color="primary" onClick={handleResetPassword} style={{ margin: "16px 0 16px 0" }} disabled={!resetCode || !newPassword}>
                  Reset Password
                </Button>
              </Box>
            </>
          )}
          <Box display="flex" justifyContent="center">
            <Button variant="text" onClick={() => setShowLogin(true)} >
              Back to Login Page
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
